<template>
  <div class="app-container">
    <Container
      :title="t('route.supplierUpdate')"
    >
      <template #main>
        <DynamicRadioForm
          ref="updateFormRef"
          v-if="isDataLoaded"
          :formModel="formModel"
          :formStruct="formStruct"
          :formRules="supplierRules"
          :groupsStruct="groupFormStruct"
          :groupsModel="groupFormModels"
          :groupsRules="bankRules"
          form-width="900"
          :labelWidth="'200px'"
          :groupLabelWidth="'150px'"
          :defaultGroup="true"
          @submit="toSubmit"
        />
      </template>
    </Container>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, toRefs, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { createModel, createModelArray } from '@/utils/component'
import { getSupplierById, updateSupplier } from '@/apis/main-data/supplier'
import Container from '@/components/form/container/Container.vue'
import DynamicRadioForm from '@/components/form/common/DynamicRadioForm.vue'
import { SupplierBankModel, SupplierInfoModel, UpdateSupplierModel } from '@/model/master-data/supplierModel'
import { ElForm, ElMessage } from 'element-plus'

export default defineComponent({
  components: {
    Container,
    DynamicRadioForm
  },
  setup() {
    const { t } = useI18n()
    const router = useRouter()
    const id = useRoute().params.id.toString()
    const updateFormRef = ref(ElForm)
    const isDataLoaded = ref(false)
    const options = [
      { label: t('common.yes'), value: true },
      { label: t('common.no'), value: false }
    ]
    const supplierStruct = [
      { prop: 'name', label: t('company.companyName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'accountNumber', label: t('supplier.supplierCode'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isForeignCurrency',
        label: t('bank.isForeignExchangeOrExternal'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: options
      },
      { prop: 'paymentTerm', label: t('bank.paymentTerms'), component: 'el-input', type: 'text', model: '', decimalPlaces: 0, isPositive: true },
      { prop: 'comment', label: t('common.notes'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isDelete',
        label: t('common.deleteStatus'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: options
      },
      { prop: 'vendorBanks', label: t('bank.bankInfo'), component: 'custom-groups' }
    ]
    const supplierRules = {
      name: [{ required: true, message: t('company.companyNameP'), trigger: 'blur' }],
      accountNumber: [{ required: true, message: t('supplier.supplierCodeP'), trigger: 'blur' }],
      paymentTerm: [{ required: true, message: t('bank.paymentTermsP'), trigger: 'blur' }]
    }
    const bankStruct = [
      { prop: 'account', label: t('bank.bankAccount'), component: 'el-input', type: 'text', model: '' },
      { prop: 'code', label: t('bank.bankCode'), component: 'el-input', type: 'text', model: '' },
      { prop: 'name', label: t('bank.bankName'), component: 'el-input', type: 'text', model: '' },
      { prop: 'city', label: t('bank.bankCity'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isCompanyAccount',
        label: t('order.isCompanyAccount'),
        component: 'el-radio-group',
        type: 'boolean',
        model: null,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      {
        prop: 'isPriorityAcceptanceBill',
        label: t('bank.isPriorityAcceptanceBill'),
        component: 'el-radio-group',
        type: 'boolean',
        model: false,
        options: [
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      }
    ]
    const bankRules = {
      account: [{ required: true, message: t('bank.bankAccountP'), trigger: 'blur' }],
      code: [{ required: true, message: t('bank.bankCodeP'), trigger: 'blur' }],
      name: [{ required: true, message: t('bank.bankNameP'), trigger: 'blur' }],
      city: [{ required: true, message: t('bank.bankCityP'), trigger: 'blur' }],
      isCompanyAccount: [{ required: true, message: t('order.isCompanyAccountP'), trigger: 'change' }],
      isPriorityAcceptanceBill: [{ required: true, message: t('bank.isPriorityAcceptanceBillP'), trigger: 'change' }]
    }
    const dataMap = reactive({
      formStruct: supplierStruct,
      formModel: createModel(supplierStruct) as SupplierInfoModel,
      groupFormStruct: bankStruct,
      groupFormModels: createModelArray(bankStruct) as SupplierBankModel[],
      async getSupplierInfo() {
        let supplierId
        try {
          supplierId = Number(id)
        } catch (error) {
          ElMessage({
            message: 'id 格式错误;' + id,
            type: 'error',
            duration: 2000
          })
          return
        }
        const data = await getSupplierById(supplierId)
        const supplierInfo = data?.data ?? null
        if (supplierInfo) {
          dataMap.formModel.id = supplierInfo.id
          dataMap.formModel.accountNumber = supplierInfo.accountNumber
          dataMap.formModel.isDelete = supplierInfo.isDelete
          dataMap.formModel.isForeignCurrency = supplierInfo.isForeignCurrency
          dataMap.formModel.name = supplierInfo.name
          dataMap.formModel.comment = supplierInfo.comment
          dataMap.formModel.paymentTerm = supplierInfo.paymentTerm
          dataMap.groupFormModels = supplierInfo.vendorBanks
          isDataLoaded.value = true
        }
      },
      async toSubmit(submitData: any) {
        const params: UpdateSupplierModel = {
          id: submitData.id,
          accountNumber: submitData.accountNumber,
          isDelete: submitData.isDelete,
          isForeignCurrency: submitData.isForeignCurrency,
          name: submitData.name,
          comment: submitData.comment,
          paymentTerm: submitData.paymentTerm,
          vendorBanks: submitData.model
        }

        const res = await updateSupplier(params)
        if (res?.data && res?.data > 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
          await router.push({ name: 'SupplierPageList', state: { refresh: true } })
        } else {
          ElMessage({
            message: res?.msg,
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    onMounted(() => {
      dataMap.getSupplierInfo()
    })
    return {
      t,
      updateFormRef,
      supplierRules,
      bankRules,
      isDataLoaded,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
